import React, { useEffect, useState, useMemo } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useCan } from "@/lib/perm";
import { useTheme } from "@/lib/theme";
import api from "@/lib/api";
import {
  Zap, LayoutDashboard, FolderKanban, ListTodo, User, BarChart3, Settings, Search,
  Plus, Sun, Moon, LogOut, ChevronsLeft, ChevronsRight, Map, Wand2, Upload, BookOpen,
  UsersRound, ScrollText, Rocket
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import CommandPalette from "./CommandPalette";
import CreateIssueDialog from "./CreateIssueDialog";
import IssueDetailDialog from "./IssueDetailDialog";
import ProfileDialog from "./ProfileDialog";
import NotificationBell from "./NotificationBell";
import { ensureRealtime, stopRealtime, useRealtime } from "@/lib/realtime";
import { initials } from "@/lib/meta";
import { cn } from "@/lib/utils";

export const AppCtx = React.createContext({ openIssue: () => {}, openCreate: () => {} });

const nav = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/my-work", icon: User, label: "My Work" },
  { to: "/projects", icon: FolderKanban, label: "Projects" },
  { to: "/issues", icon: ListTodo, label: "Issues" },
  { to: "/roadmap", icon: Map, label: "Roadmap" },
  { to: "/pages", icon: BookOpen, label: "Pages" },
  { to: "/teams", icon: UsersRound, label: "Teams" },
  { to: "/reports", icon: BarChart3, label: "Reports" },
  { to: "/automations", icon: Wand2, label: "Automations" },
  { to: "/import", icon: Upload, label: "Import" },
  { to: "/audit", icon: ScrollText, label: "Audit", adminOnly: true },
  { to: "/admin", icon: Settings, label: "Admin", adminOnly: true },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const isAdmin = useCan("*");
  const navigate = useNavigate();
  const location = useLocation();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [issueId, setIssueId] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [collapsed, setCollapsed] = useState(false);

  const loadRefs = async () => {
    const [p, u] = await Promise.all([api.get("/projects"), api.get("/users")]);
    setProjects(p.data);
    setUsers(u.data);
  };
  useEffect(() => { loadRefs(); ensureRealtime(); return () => stopRealtime(); }, []);

  // Bridge SSE events -> global window CustomEvent so per-page components can listen
  useRealtime((ev) => {
    window.dispatchEvent(new CustomEvent("ft:realtime", { detail: ev }));
    if (ev.type === "issue.created" || ev.type === "issue.updated" || ev.type === "issue.deleted") {
      window.dispatchEvent(new Event("ft:issues-changed"));
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault(); setCmdOpen(true);
      } else if (e.key.toLowerCase() === "c" && !["INPUT","TEXTAREA"].includes(document.activeElement?.tagName) && !document.activeElement?.isContentEditable) {
        e.preventDefault(); setCreateOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const ctx = useMemo(() => ({
    openIssue: (id) => setIssueId(id),
    openCreate: () => setCreateOpen(true),
    projects, users, refreshRefs: loadRefs,
  }), [projects, users]);

  return (
    <AppCtx.Provider value={ctx}>
      <div className="flex h-screen overflow-hidden bg-background">
        <aside className={cn("border-r border-border bg-secondary/30 flex flex-col shrink-0 transition-[width] duration-150", collapsed ? "w-14" : "w-60")}>
          <div className="h-14 flex items-center gap-2 px-4 border-b border-border">
            <div className="w-7 h-7 grid place-items-center rounded-md bg-primary text-primary-foreground shrink-0"><Zap className="w-4 h-4" /></div>
            {!collapsed && <span className="font-display font-semibold tracking-tight">DIGI-JIRA</span>}
          </div>
          <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
            {nav.filter((n) => !n.adminOnly || isAdmin).map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}
                className={({isActive}) => cn(
                  "flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm hover:bg-accent hover:text-accent-foreground transition-colors",
                  isActive && "bg-accent text-accent-foreground font-medium"
                )}>
                <n.icon className="w-4 h-4 shrink-0" />
                {!collapsed && <span>{n.label}</span>}
              </NavLink>
            ))}
            {!collapsed && (
              <div className="pt-4">
                <div className="px-2.5 text-[10px] uppercase tracking-widest text-muted-foreground/70 mb-1.5">Projects</div>
                {projects.slice(0, 8).map(p => (
                  <NavLink key={p.id} to={`/projects/${p.id}`} data-testid={`nav-project-${p.key}`}
                    className={({isActive}) => cn(
                      "flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors truncate",
                      isActive && "bg-accent text-foreground"
                    )}>
                    <span className="w-5 h-5 grid place-items-center rounded bg-primary/15 text-primary text-[10px] font-mono font-semibold shrink-0">{p.key.slice(0,2)}</span>
                    <span className="truncate">{p.name}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </nav>
          <button onClick={()=>setCollapsed(!collapsed)} className="h-9 border-t border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors flex items-center justify-center" data-testid="sidebar-collapse">
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
          </button>
        </aside>
        <div className="flex-1 flex flex-col overflow-hidden">
          <header className="h-14 border-b border-border flex items-center gap-3 px-5 shrink-0 bg-background/95">
            <button onClick={()=>setCmdOpen(true)} data-testid="topbar-search"
              className="flex-1 max-w-md flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-secondary/60 hover:bg-accent transition-colors text-sm text-muted-foreground">
              <Search className="w-4 h-4" />
              <span>Search issues, projects…</span>
              <span className="ml-auto flex items-center gap-1"><kbd>⌘</kbd><kbd>K</kbd></span>
            </button>
            <div className="flex-1" />
            <Button size="sm" onClick={()=>setCreateOpen(true)} data-testid="topbar-create-issue" className="gap-1.5">
              <Plus className="w-4 h-4" /> Create
            </Button>
            <Button size="icon" variant="ghost" onClick={toggle} data-testid="theme-toggle" className="h-9 w-9">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="rounded-full outline-none focus-visible:ring-2 ring-ring">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.avatar_url} alt={user?.name} />
                    <AvatarFallback className="text-xs">{initials(user?.name)}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={()=>navigate("/my-work")}>My Work</DropdownMenuItem>
                <DropdownMenuItem onClick={()=>setProfileOpen(true)} data-testid="user-profile-btn">Profile & Security</DropdownMenuItem>
                {isAdmin && <DropdownMenuItem onClick={()=>navigate("/admin")}>Admin</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>
          <main className="flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
      <CommandPalette open={cmdOpen} setOpen={setCmdOpen} onOpenIssue={(id)=>setIssueId(id)} projects={projects} />
      <CreateIssueDialog open={createOpen} setOpen={setCreateOpen} projects={projects} users={users} onCreated={()=>{}} />
      <IssueDetailDialog issueId={issueId} setIssueId={setIssueId} users={users} projects={projects} />
      <ProfileDialog open={profileOpen} setOpen={setProfileOpen} user={user} onUpdated={loadRefs} />
    </AppCtx.Provider>
  );
}

export const useApp = () => React.useContext(AppCtx);
